// LLM tracing contracts (spans, flame graphs, aggregate charts).
//
// A TraceSpan is the generic observability unit for ANY LLM activity in
// cortex — one-shot workflow agents, persistent agents, theopolis chat
// agents, eval judges, orchestrator matchers, embeddings. Spans nest via
// `parentSpanId` under a shared `traceId`, so a trace renders as a flame
// graph / waterfall. Producers:
//
//   1. Mastra-emitted spans — @cortex/mastra-layer's trace exporter hooks
//      Mastra's native observability stream (agent runs, model generations,
//      tool calls, workflow steps) and normalizes them to this shape.
//   2. Manual spans — LLM calls that bypass Mastra agents (structured-output
//      judges, embeddings) emit spans through the mastra-layer tracer.
//
// Cross-package contract: schemas live here; types are inferred.
import { z } from 'zod';
import { paginatedSchema } from './pagination.js';
// ─── Core span vocabulary ────────────────────────────────────────────────────
/**
 * Where a trace originated. Stamped by the producing host so the UI can
 * filter "persistent-agent traces" vs "workflow traces" without joins.
 */
export const TraceSourceSchema = z.enum([
    /** Workflow run executed by apps/runner (llm-agent / compiled-agent nodes). */
    'workflow',
    /** Long-lived persistent agent woken in apps/persistent-agent-host. */
    'persistent-agent',
    /** Interactive chat agent (theopolis). */
    'chat',
    /** Eval-harness judge / grader calls. */
    'evaluation',
    /** Orchestrator dedup matchers + embeddings. */
    'orchestrator',
    /** Anything else (manual spans from new hosts default here). */
    'other',
]);
/**
 * Span type. Mastra span types pass through verbatim (`agent_run`,
 * `model_generation`, `tool_call`, `mcp_tool_call`, `workflow_run`,
 * `workflow_step`, …); manual spans use cortex-owned values
 * (`structured_output`, `embedding`, `generic`). Kept as an open string so a
 * Mastra upgrade that adds span types never breaks ingestion — consumers
 * bucket unknown values under "other".
 */
export const TraceSpanTypeSchema = z.string().min(1);
/**
 * Well-known span types the console groups/colors by. Not exhaustive —
 * see TraceSpanTypeSchema.
 */
export const wellKnownTraceSpanTypes = [
    'agent_run',
    'model_generation',
    'model_step',
    'tool_call',
    'mcp_tool_call',
    'workflow_run',
    'workflow_step',
    'memory_operation',
    'structured_output',
    'embedding',
    'generic',
];
export const TraceSpanStatusSchema = z.enum(['running', 'ok', 'error']);
/**
 * Links a span back to the domain entity that caused it. `orgId` is
 * mandatory (multi-tenancy); everything else is optional and
 * source-dependent. Attribution is attached to the ROOT span by the call
 * site (via tracing metadata) and inherited by every child span at export
 * time, so each persisted row is independently org-scopable.
 */
export const TraceAttributionSchema = z.object({
    orgId: z.string().min(1),
    source: TraceSourceSchema,
    runId: z.string().optional(),
    nodeExecutionId: z.string().optional(),
    evaluationId: z.string().optional(),
    persistentAgentId: z.string().optional(),
    /** Chat/session identifier for interactive sources (theopolis). */
    sessionId: z.string().optional(),
});
// ─── Token usage (per span) ──────────────────────────────────────────────────
export const TraceTokenUsageSchema = z.object({
    tokensIn: z.number().int().nonnegative().default(0),
    tokensOut: z.number().int().nonnegative().default(0),
    cacheReadTokens: z.number().int().nonnegative().default(0),
    cacheWriteTokens: z.number().int().nonnegative().default(0),
    reasoningTokens: z.number().int().nonnegative().default(0),
});
// ─── Write shape (producer → sink) ───────────────────────────────────────────
/**
 * One span as handed to a trace sink (the persistence boundary —
 * mastra-layer stays Prisma-free; hosts implement the sink with Prisma).
 * Upsert key is (traceId, spanId): a span may be written once at start
 * (status `running`) and again at end with final timings/usage.
 */
export const TraceSpanWriteSchema = z.object({
    spanId: z.string().min(1),
    traceId: z.string().min(1),
    parentSpanId: z.string().optional(),
    name: z.string().min(1),
    spanType: TraceSpanTypeSchema,
    status: TraceSpanStatusSchema,
    attribution: TraceAttributionSchema,
    /** Mastra entity that produced the span (agent / workflow / tool …). */
    entityType: z.string().optional(),
    entityId: z.string().optional(),
    entityName: z.string().optional(),
    /** Model id for LLM spans (e.g. `claude-sonnet-4-5`). */
    model: z.string().optional(),
    /** Provider string as reported by the SDK (e.g. `anthropic`). */
    provider: z.string().optional(),
    usage: TraceTokenUsageSchema.optional(),
    /** Estimated USD for LLM spans (pricing table lookup; 0 when unknown). */
    costUsd: z.number().nonnegative().optional(),
    startedAt: z.coerce.date(),
    endedAt: z.coerce.date().optional(),
    /** Serialized (truncated) input/output payloads. */
    input: z.unknown().optional(),
    output: z.unknown().optional(),
    error: z.record(z.unknown()).optional(),
    /** Span-type specific attributes (finishReason, maxSteps, toolType, …). */
    attributes: z.record(z.unknown()).optional(),
    /** Free-form metadata propagated from the call site. */
    metadata: z.record(z.unknown()).optional(),
});
// ─── API read shapes ─────────────────────────────────────────────────────────
/** One persisted span, as returned by `GET /v1/traces/:traceId`. */
export const TraceSpanSchema = z.object({
    spanId: z.string(),
    traceId: z.string(),
    parentSpanId: z.string().nullable(),
    name: z.string(),
    spanType: TraceSpanTypeSchema,
    status: TraceSpanStatusSchema,
    source: TraceSourceSchema,
    runId: z.string().nullable(),
    nodeExecutionId: z.string().nullable(),
    evaluationId: z.string().nullable(),
    persistentAgentId: z.string().nullable(),
    sessionId: z.string().nullable(),
    entityType: z.string().nullable(),
    entityId: z.string().nullable(),
    entityName: z.string().nullable(),
    model: z.string().nullable(),
    provider: z.string().nullable(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    cacheReadTokens: z.number().int().nonnegative(),
    cacheWriteTokens: z.number().int().nonnegative(),
    reasoningTokens: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative().nullable(),
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    /** endedAt − startedAt; null while running. */
    durationMs: z.number().int().nonnegative().nullable(),
    input: z.unknown().nullable(),
    output: z.unknown().nullable(),
    error: z.record(z.unknown()).nullable(),
    attributes: z.record(z.unknown()).nullable(),
    metadata: z.record(z.unknown()).nullable(),
});
/** Full trace — every span sharing a traceId. Client builds the flame graph. */
export const TraceDetailSchema = z.object({
    traceId: z.string(),
    spans: z.array(TraceSpanSchema),
});
/**
 * One trace in the list view — the root span plus rolled-up totals over
 * its children (computed server-side so the list needs no N+1 fetches).
 */
export const TraceListItemSchema = z.object({
    traceId: z.string(),
    rootSpanId: z.string(),
    name: z.string(),
    spanType: TraceSpanTypeSchema,
    status: TraceSpanStatusSchema,
    source: TraceSourceSchema,
    runId: z.string().nullable(),
    persistentAgentId: z.string().nullable(),
    evaluationId: z.string().nullable(),
    sessionId: z.string().nullable(),
    entityName: z.string().nullable(),
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    spanCount: z.number().int().nonnegative(),
    llmCallCount: z.number().int().nonnegative(),
    toolCallCount: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    /** Distinct models used anywhere in the trace. */
    models: z.array(z.string()),
});
/** Query string for `GET /v1/traces`. */
export const TraceListQuerySchema = z
    .object({
    source: TraceSourceSchema.optional(),
    status: TraceSpanStatusSchema.optional(),
    runId: z.string().min(1).optional(),
    persistentAgentId: z.string().min(1).optional(),
    evaluationId: z.string().min(1).optional(),
    sessionId: z.string().min(1).optional(),
    model: z.string().min(1).optional(),
    since: z.string().datetime().optional(),
    until: z.string().datetime().optional(),
    limit: z.number().int().positive().max(200).optional(),
    offset: z.number().int().nonnegative().optional(),
})
    .strict();
export const TraceListResponseSchema = paginatedSchema(TraceListItemSchema);
// ─── Aggregates (charts) ─────────────────────────────────────────────────────
export const TraceTimeseriesBucketSchema = z.enum(['hour', 'day']);
/**
 * Query string for `GET /v1/trace-timeseries`. Mirrors run-timeseries:
 * rolling `windowHours` (clamped server-side to [1, 720]) unless
 * `since`/`until` provide a custom range. Optional filters narrow every
 * aggregate.
 */
export const TraceTimeseriesQuerySchema = z
    .object({
    bucket: TraceTimeseriesBucketSchema.optional(),
    windowHours: z.number().int().positive().max(720).optional(),
    since: z.string().datetime().optional(),
    until: z.string().datetime().optional(),
    source: TraceSourceSchema.optional(),
    model: z.string().min(1).optional(),
    persistentAgentId: z.string().min(1).optional(),
})
    .strict();
/** One time bucket of LLM activity (aggregated over model_generation spans + trace roots). */
export const TraceTimeseriesBucketRowSchema = z.object({
    at: z.string().datetime(),
    traces: z.number().int().nonnegative(),
    llmCalls: z.number().int().nonnegative(),
    toolCalls: z.number().int().nonnegative(),
    errors: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    cacheReadTokens: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    /** Mean + p95 latency of LLM spans in the bucket, ms. */
    avgLlmDurationMs: z.number().nonnegative(),
    p95LlmDurationMs: z.number().nonnegative(),
});
/** Per-model rollup over the window — drives the "by model" breakdown charts. */
export const TraceModelRowSchema = z.object({
    model: z.string(),
    provider: z.string().nullable(),
    llmCalls: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    avgDurationMs: z.number().nonnegative(),
    p95DurationMs: z.number().nonnegative(),
});
/** Per-source rollup over the window (workflow vs persistent-agent vs …). */
export const TraceSourceRowSchema = z.object({
    source: TraceSourceSchema,
    traces: z.number().int().nonnegative(),
    llmCalls: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
});
/** Named entity rollup (workflow or persistent agent) — top-N by cost. */
export const TraceEntityCostRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    traces: z.number().int().nonnegative(),
    llmCalls: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
});
export const TraceTimeseriesResponseSchema = z.object({
    bucket: TraceTimeseriesBucketSchema,
    windowHours: z.number().int().positive(),
    buckets: z.array(TraceTimeseriesBucketRowSchema),
    byModel: z.array(TraceModelRowSchema),
    bySource: z.array(TraceSourceRowSchema),
    /** Top workflows by cost in the window (via run attribution). */
    byWorkflow: z.array(TraceEntityCostRowSchema),
    /** Top persistent agents by cost in the window. */
    byPersistentAgent: z.array(TraceEntityCostRowSchema),
    /**
     * Distinct model ids observed in the window for the filter dropdown.
     * Ignores the `model` query filter so the picker stays fully populated
     * while a single model is selected.
     */
    models: z.array(z.string()),
});
// ─── Cross-org platform costs (Formant staff only) ──────────────────────────
/**
 * Who pays the model provider for an LLM call:
 * - `platform` — Formant's provider keys (pod env keys; also every
 *   embedding and every Bedrock / Vertex call, which never use org creds).
 * - `customer` — credentials the org stored on its own `model_config`.
 *
 * Attributed at read time (trace_span carries no key marker): a span is
 * `customer` when its org has a credentialed model config whose provider
 * and model id match the span. See `classifyKeySource` in the API.
 */
export const TraceKeySourceSchema = z.enum(['platform', 'customer']);
/** Query string for `GET /v1/admin/platform-costs`. Same window rules as the org view. */
export const CrossOrgCostsQuerySchema = z
    .object({
    bucket: TraceTimeseriesBucketSchema.optional(),
    windowHours: z.number().int().positive().max(720).optional(),
    since: z.string().datetime().optional(),
    until: z.string().datetime().optional(),
})
    .strict();
/** Cost + call totals split by who pays the provider. */
export const KeySourceTotalsSchema = z.object({
    costUsd: z.number().nonnegative(),
    llmCalls: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    platformCostUsd: z.number().nonnegative(),
    platformLlmCalls: z.number().int().nonnegative(),
    customerCostUsd: z.number().nonnegative(),
    customerLlmCalls: z.number().int().nonnegative(),
});
/** One org × model × key-source rollup. */
export const CrossOrgModelUsageSchema = z.object({
    model: z.string(),
    provider: z.string().nullable(),
    keySource: TraceKeySourceSchema,
    llmCalls: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
});
export const CrossOrgOrgRowSchema = KeySourceTotalsSchema.extend({
    orgId: z.string(),
    /** Keycloak org name; null when the org isn't a Keycloak org (e.g. robotics-only). */
    orgName: z.string().nullable(),
    /** Models this org used in the window, cost-descending. */
    models: z.array(CrossOrgModelUsageSchema),
});
/** One model × key-source rollup across every org. */
export const CrossOrgModelRowSchema = CrossOrgModelUsageSchema.extend({
    orgCount: z.number().int().nonnegative(),
});
export const CrossOrgCostsBucketRowSchema = z.object({
    at: z.string().datetime(),
    platformCostUsd: z.number().nonnegative(),
    customerCostUsd: z.number().nonnegative(),
    llmCalls: z.number().int().nonnegative(),
});
export const CrossOrgCostsResponseSchema = z.object({
    bucket: TraceTimeseriesBucketSchema,
    windowHours: z.number().int().positive(),
    summary: KeySourceTotalsSchema.extend({ orgCount: z.number().int().nonnegative() }),
    buckets: z.array(CrossOrgCostsBucketRowSchema),
    /** Every org with LLM activity in the window, cost-descending. */
    orgs: z.array(CrossOrgOrgRowSchema),
    /** Model × key-source rollups across orgs, cost-descending. */
    byModel: z.array(CrossOrgModelRowSchema),
});
//# sourceMappingURL=trace.js.map